/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { buildForterSnippet } from '@/lib/forter/snippet';

type ForterScriptProps = {
    enabled?: boolean;
    siteId?: string;
    nonce?: string;
};

/**
 * Sitewide Forter device-token snippet. Renders nothing unless Forter is enabled
 * and a site id is configured. The script id matches the site id, as in SFRA.
 */
export function ForterScript({ enabled, siteId, nonce }: ForterScriptProps) {
    const id = siteId?.trim() ?? '';
    const html = enabled === true && id ? buildForterSnippet(id) : undefined;
    if (!html) return null;

    return (
        // Vendor collector must stay inline so the site id and CSP flag match this request.
        // eslint-disable-next-line react/no-danger
        <script id={id} nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: html }} />
    );
}
